/**
 * Контракты режимов «Цифры», «Пунктуация» и «Специальные символы»
 * (ТЗ §6, режимы 8–10).
 *
 * Три режима, а механизм один: клавиши открываются группами, и следующая
 * группа ждёт, пока предыдущая не начнёт получаться. Различается материал —
 * цифровой ряд, знаки препинания внутри естественных предложений и символы,
 * которые набираются с Shift.
 */

import type {
  HintLevel,
  LanguageCode,
  LayoutId,
  MasteryStat,
  MasteryUnit,
  OsPlatform,
  TrainingMode,
} from '@type2x/shared-contracts';
import type { Finger } from '@type2x/shared-contracts';

/** Режимы, которые ведёт эта функция. */
export const SYMBOL_MODES = ['digits', 'punctuation', 'special-characters'] as const;
export type SymbolMode = (typeof SYMBOL_MODES)[number];

/**
 * Режимы функции — подмножество режимов обучения.
 *
 * Проверка на уровне типов: итог сессии уходит на сервер с этим значением, и
 * режим, которого нет в общем перечне, не был бы учтён в разнообразии.
 */
const MODES_ARE_TRAINING_MODES: readonly TrainingMode[] = SYMBOL_MODES;
void MODES_ARE_TRAINING_MODES;

/** Снимок навыка, из которого считается открытость групп. */
export interface SymbolsSnapshot {
  readonly language: LanguageCode;
  readonly layoutId: LayoutId;
  /** Операционная система: от неё зависит порядок специальных символов. */
  readonly platform: OsPlatform;
  readonly stats: Readonly<Record<string, MasteryStat>>;
}

/** Группа символов одного режима. */
export interface SymbolGroup {
  readonly id: string;
  readonly mode: SymbolMode;
  /** Символы группы в порядке открытия. */
  readonly characters: readonly string[];
}

/** Подсказка о Shift: какой символ и какую клавишу он меняет. */
export interface ShiftHint {
  readonly character: string;
  /** Символ той же клавиши без Shift. */
  readonly base: string;
  readonly finger: Finger;
}

/** Упражнение режима. */
export interface SymbolsExercise {
  readonly seed: string;
  /** Элементы: группы цифр, сочетания с Shift либо целые предложения. */
  readonly items: readonly string[];
  /** Текст для набора: элементы, соединённые пробелами. */
  readonly text: string;
  /**
   * Состоит ли упражнение из естественных предложений.
   *
   * Знаки препинания тренируются внутри текста: запятая в отрыве от
   * предложения не тренирует решение, где её ставить (ТЗ §6, режим 9).
   */
  readonly usesSentences: boolean;
}

/** План занятия режима. */
export interface SymbolsPlan {
  readonly mode: SymbolMode;
  readonly language: LanguageCode;
  readonly layoutId: LayoutId;
  /** Группа, которую пользователь осваивает сейчас; null — открыты все. */
  readonly group: SymbolGroup | null;
  readonly completedGroupIds: readonly string[];
  /** Сколько всего групп в режиме: по нему считается доля пройденного. */
  readonly totalGroups: number;
  /** Сколько всего символов в режиме: по нему видно, сколько уже открыто. */
  readonly totalCharacters: number;
  /** Символы режима, открытые к этому моменту. */
  readonly unlockedCharacters: readonly string[];
  /** Учебные единицы символов, встречающихся в упражнении. */
  readonly units: readonly MasteryUnit[];
  readonly exercise: SymbolsExercise;
  /** Символы упражнения, которые требуют Shift. */
  readonly shiftHints: readonly ShiftHint[];
  readonly hintLevel: HintLevel;
  /** Все группы пройдены: режиму больше нечему учить. */
  readonly finished: boolean;
}

/** Сколько символов открывается за раз в режимах пунктуации и символов. */
export const GROUP_SIZE = 2;

/** Длина группы цифр: число из трёх цифр — это движение, а не цифра. */
export const DIGIT_CHUNK_LENGTH = 3;

/** Сколько элементов в одном упражнении режима. */
export const ITEMS_PER_EXERCISE: Readonly<Record<SymbolMode, number>> = {
  digits: 12,
  // Предложение длиннее сочетания в несколько раз: трёх хватает на занятие.
  punctuation: 3,
  'special-characters': 10,
};

/**
 * Уровень подсказок режима.
 *
 * Цифры и специальные символы ищутся глазами, пока не найдена клавиша, —
 * клавиатура нужна. Пунктуация тренируется внутри текста: там задача не
 * найти клавишу, а не потерять знак, поэтому подсказка появляется с
 * задержкой (ТЗ §7.5).
 */
export const HINT_LEVEL: Readonly<Record<SymbolMode, HintLevel>> = {
  digits: 'full-keyboard-and-finger',
  punctuation: 'delayed-hint',
  'special-characters': 'full-keyboard-and-finger',
};

/**
 * Символы, которые для этой операционной системы важнее прочих.
 *
 * «Символы выбранной ОС» (ТЗ §6, режим 10) — это не разные клавиши, а разная
 * нужда: путь в Windows пишется через обратную косую черту и переменную
 * `%PATH%`, а в macOS и Linux — через тильду, косую черту и `$`. Перечислены
 * только приоритеты: сам набор по-прежнему берётся из раскладки, и символ,
 * которого в ней нет, в режим не попадёт.
 */
export const PLATFORM_PRIORITY_SYMBOLS: Readonly<Record<OsPlatform, readonly string[]>> = {
  windows: ['\\', '%', '&'],
  macos: ['~', '/', '$'],
  linux: ['~', '/', '$', '|'],
};
